import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import {
  API,
  BookingDTO,
  CampaignWithPreviewDTO,
  CommercialWithPreviewDTO,
  CompanyDTO,
  CullDTO,
  MediumDTO,
  ReportingFullDTO,
  RequirementDTO,
  RequirementFullDTO,
} from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { CampaignReportingService } from '../../../../services/campaign-reporting.service';
import { UtilService } from '../../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../../services/tag.service';
import { RequirementReportingDTOAndTags } from '../../../../models/taggable.model';
import { DiscussionService } from '../../../../services/discussion.service';
import { ModalNavigationComponent } from '../../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../../services/gui/modal/modal-navigation.service';
import { ReportingModalComponent } from '../../../../components/standard-reporting/reporting-modal/reporting-modal.component';
import { ReportingService } from '../../../../services/reporting.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SbLocalDateTime } from '../../../../services/api/api-date-time';
import { PermissionService } from '../../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { QueryParamService } from '../../../../services/lists/query-params';
import { MsOptions, Mutable } from '../../../../services/global/global-interfaces';
import { FilterStorageService } from '../../../../services/filter-storage.service';
import { from, of, switchMap } from 'rxjs';
import { BookingService } from '../../../../services/booking.service';

export interface RequirementReportingDTOWithHoverAndTags extends RequirementReportingDTOAndTags {
  isHover?: boolean;
}

@Component({
  selector: 'sb-page-requirements-list',
  templateUrl: './requirements-list.component.html',
  styleUrls: ['./requirements-list.component.scss'],
  standalone: false,
})
export class RequirementsListComponent implements OnInit, ModalNavigationComponent {
  isInModalContext: boolean;

  managedCompany: CompanyDTO;
  campaign: CampaignWithPreviewDTO;
  commercial: CommercialWithPreviewDTO;
  requirements: RequirementReportingDTOWithHoverAndTags[];
  selectedRequirements: RequirementReportingDTOWithHoverAndTags[];
  selectedCompany: number;
  campaignId: number;
  commercialId: number;
  loading: boolean;
  ghosts: any[];
  exportState: boolean;
  form = new FormGroup({
    Title: new FormControl('', [Validators.required]),
  });
  reportName: string;
  reportTill: SbLocalDateTime;
  reportFrom: SbLocalDateTime;

  contentCulls: CullDTO[];
  queryParamsParsed: boolean;
  selectedContentCulls: CullDTO[];
  msContentCullOptions: MsOptions;

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public errorHandler: ErrorService,
    public modal: ModalService,
    private campaignsService: CampaignReportingService,
    public util: UtilService,
    public translate: TranslateService,
    public tagService: TagService,
    public discussionService: DiscussionService,
    private modalNavigation: ModalNavigationService,
    private reportingService: ReportingService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef,
    public queryParamService: QueryParamService,
    private filterStorage: FilterStorageService,
    private readonly bookingService: BookingService
  ) {
    this.exportState = false;
    this.reportName = null;
    this.managedCompany = undefined;
    this.campaign = undefined;
    this.commercial = undefined;
    this.requirements = undefined;
    this.selectedRequirements = [];
    this.selectedCompany = undefined;
    this.campaignId = undefined;
    this.commercialId = undefined;
    this.loading = true;
    this.ghosts = [];
    this.contentCulls = [];
    this.queryParamsParsed = false;
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe((params: Params) => {
      if (this.isInModalContext) {
        this.selectedRequirements = this.modalNavigation.getCurrentNavigation()?.data?.selectedRequirements;
        this.campaignId = this.modalNavigation.getCurrentNavigation()?.data?.campaignId;
        this.commercialId = this.modalNavigation.getCurrentNavigation()?.data?.commercialId;
      } else {
        this.campaignId = +params.campaignID;
        this.commercialId = +params.commercialID;
      }
      if (!this.queryParamsParsed && !this.isInModalContext) {
        this.filterStorage.fetch('COMMERCIAL');
        this.parseQueryParams().then(() => {
          this.queryParamsParsed = true;
          this.refreshFromServer(this.campaignId, this.commercialId);
        });
      } else {
        this.refreshFromServer(this.campaignId, this.commercialId);
      }
    });
  }

  refreshFromServer(campaignID?: number, commercialID?: number) {
    this.loading = true;

    this.api
      .ds_cm_Campaign_retrieveByIdFrontendEnhanced(campaignID || this.campaign.id)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (campaignData: CampaignWithPreviewDTO) => {
          if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
            this.campaign = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.Campaign, [campaignData])[0];
          } else {
            this.campaign = campaignData;
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });

    this.api
      .ds_cm_Commercial_retrieveByIdFrontendEnhanced(commercialID || this.commercial.id)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (commercialData: CommercialWithPreviewDTO) => {
          if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
            this.commercial = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.Commercial, [commercialData])[0];
          } else {
            this.commercial = commercialData;
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });

    this.api
      .ds_cm_Commercial_retrieveRequirements(commercialID || this.commercial.id, { contentCull: this.selectedContentCullIds })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (requirementData: RequirementFullDTO[]) => {
          if (this.permissionService.hasPermission(this.permissionService.Permission.ds_cm_reporting)) {
            this.requirements = this.campaignsService.getAndMergePulseChart(requirementData);
          } else {
            this.requirements = requirementData;
          }
          if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
            this.requirements = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.Requirement, this.requirements);
          }
          if (this.requirements) {
            this.requirements = this.buildHover(this.requirements);
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  toggleRequirementEnabled(requirement: RequirementDTO, e: boolean) {
    let modalMessage: string;
    let modalTitle: string;

    if (e === false) {
      modalTitle = this.translate.instant('lang.requirements.disableRequirement');
      modalMessage = this.translate.instant('lang.requirements.disableRequirementQuestion');
    } else {
      modalTitle = this.translate.instant('lang.requirements.enableRequirement');
      modalMessage = this.translate.instant('lang.requirements.enableRequirementQuestion');
    }
    this.modal
      .confirm({
        title: modalTitle,
        message: modalMessage,
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          requirement.active = e;
          this.api
            .ds_cm_Requirement_editRequirement(requirement)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {},
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        } else {
          requirement.active = !e;
        }
      });
  }

  deleteRequirement(requirement: RequirementDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.requirements.deleteRequirement'),
        message: this.translate.instant('lang.requirements.deleteRequirementQuestion', { requirement: requirement.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ds_cm_Requirement_deleteById(requirement.id)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.spliceRequirement(requirement);
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  spliceRequirement(requirement: RequirementDTO) {
    const index = this.requirements.findIndex(x => x.id === requirement.id);
    if (index !== -1) {
      this.requirements.splice(index, 1);
    }
  }

  buildHover(requirements: RequirementReportingDTOWithHoverAndTags[]) {
    for (const requirement of requirements) {
      requirement.isHover = false;
    }
    return requirements;
  }

  cullCheck() {
    if (this.requirements?.length === 1) {
      return this.requirements[0].geoCull || this.requirements[0].playerCull || this.requirements[0].timeCull;
    }
    return false;
  }

  // Modal actions
  selectRequirement(requirement: RequirementReportingDTOWithHoverAndTags) {
    if (this.isInModalContext) {
      const found = this.util.arrayToIdArray(this.selectedRequirements)?.indexOf(requirement.id);
      if (this.util.arrayToIdArray(this.selectedRequirements) && found !== -1) {
        this.selectedRequirements.splice(found, 1);
      } else {
        this.selectedRequirements.push(requirement);
      }
    }
  }

  returnSelectedRequirements() {
    if (!this.exportState) {
      this.exportState = true;
      this.registerFormChangesListener();
    } else {
      this.exportState = false;
      this.modalNavigation.result({
        requirements: this.selectedRequirements as RequirementFullDTO[],
        from: this.reportFrom,
        till: this.reportTill,
        name: this.reportName,
      });
      this.reportFrom = null;
      this.reportTill = null;
      this.reportName = null;
    }
  }

  deselectRequirements() {
    this.selectedRequirements = [];
  }

  selectionForExport() {
    const initialRequirements = this.selectedRequirements?.length > 0 ? this.selectedRequirements?.slice() : undefined;
    this.modal
      .showNavigationModal({
        entryComponent: {
          value: RequirementsListComponent,
          data: {
            selectedRequirements: initialRequirements || [],
            modalType: 'requirementSelect',
            campaignId: this.campaignId,
            commercialId: this.commercialId,
          },
        },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: any) => {
        if (result?.requirements?.length > 0) {
          this.api
            .ds_cm_Campaign_report({
              requirement: this.util.arrayToIdArray(result.requirements),
              from: result.from,
              till: result.till,
              reportName: result.name,
            })
            .subscribe((rep: ReportingFullDTO) => {
              this.reportingService.addReport(rep);
              this.modal
                .showNavigationModal({
                  entryComponent: {
                    value: ReportingModalComponent,
                    data: rep,
                  },

                  title: this.translate.instant('lang.globals.download'),
                  modalOptions: { windowClass: 'M' },
                })
                .then(() => {})
                .catch(() => {});
            });
        }
      })
      .catch(() => {});
  }

  parseQueryParams(): Promise<void> {
    return new Promise(res => {
      try {
        let contentCullIds: number[] = [];
        if (this.permissionService.hasPermission(this.permissionService.Permission.cull_list)) {
          contentCullIds = this.filterStorage.get('COMMERCIAL', 'contentCulls') ?? [];
        } else {
          this.filterStorage.clear('COMMERCIAL', 'contentCulls');
        }

        if (contentCullIds?.length > 0) {
          this.api
            .cs_Cull_retrieveAll({ id: contentCullIds })
            .pipe(take(1))
            .subscribe({
              next: culls => {
                this.selectedContentCulls = culls;
                res();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        } else {
          res();
        }
      } catch (e) {}
    });
  }

  update($event: any, type: string) {
    switch (type) {
      case 'CONTENT_CULLS':
        if ($event && $event?.length > 0) {
          this.selectedContentCulls = $event;
          this.filterStorage.set('COMMERCIAL', 'contentCulls', this.util.arrayToIdArray(this.selectedContentCulls));
        } else {
          this.selectedContentCulls = [];
          this.filterStorage.clear('COMMERCIAL', 'contentCulls');
        }
        break;
    }
    this.refreshFromServer();
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'CONTENT_CULLS':
        if (options) {
          this.msContentCullOptions = options;
        }
        if (this.msContentCullOptions?.pagination) {
          this.api
            .cs_Cull_retrieveAll(
              {
                search: this.util.encodeSearchQueryParam(false, this.msContentCullOptions.search),
                type: 'ContentCull',
              },
              this.msContentCullOptions.pagination
            )
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: (culls: CullDTO[]) => {
                this.contentCulls = culls;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
    }
  }

  get Title() {
    return this.form.get('Title');
  }

  private registerFormChangesListener() {
    this.Title.valueChanges.subscribe(value => {
      this.reportName = value;
    });
  }

  localDateTimeInputFrom(date: SbLocalDateTime) {
    this.reportFrom = date;
  }

  localDateTimeInputTill(date: SbLocalDateTime) {
    this.reportTill = date;
  }

  get selectedContentCullIds(): number[] {
    return this.selectedContentCulls ? this.util.arrayToIdArray(this.selectedContentCulls) : [];
  }

  protected copyRequirementModal(requirement: RequirementFullDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.globals.copyRequirement'),
        message: this.translate.instant('lang.globals.copyRequirement.message'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then(result => {
        if (result) {
          this.copyRequirement(requirement);
        }
      })
      .catch(() => {});
  }

  private copyRequirement(requirement: RequirementFullDTO) {
    this.loading = true;
    let r: RequirementFullDTO = new RequirementFullDTO();
    r.name = requirement.name;
    r.active = true;
    r.timeCull = requirement?.timeCull ?? null;
    r.playerCull = requirement?.playerCull ?? null;
    r.geoCull = requirement.geoCull ?? null;
    this.api
      .ds_cm_Commercial_createRequirement(this.commercialId, r)
      .pipe(
        switchMap(createdReq => {
          r = createdReq;
          return this.api.ds_cm_Requirement_triggerRecalculateBookings(createdReq.id);
        }),
        switchMap(bookingRecalculation => {
          return of(this.bookingService.handleBookingRefresh(r.bookings, bookingRecalculation));
        }),
        switchMap((createdBookings: BookingDTO[]) => {
          // check if requirement count from original and copy match

          r.bookings = createdBookings;
          // set Media to bookings
          const previewMediaSet: Set<MediumDTO> = new Set();
          requirement.bookings.forEach(reqBooking => {
            const booking = r.bookings.find(lookUpBooking => lookUpBooking.channel?.id === reqBooking.channel?.id);
            booking.excluded = reqBooking.excluded;
            if (reqBooking.medium) {
              booking.medium = {
                ...reqBooking.medium,
              };
              previewMediaSet.add({ ...reqBooking.medium });
            }
          });
          // Mutable cast for editable since previewMedia is readonly
          (r as Mutable<RequirementFullDTO>).previewMedia = Array.from(previewMediaSet.values());
          return from(this.bookingService.editBookings(r.bookings));
        }),
        switchMap(editedBookings => {
          r.bookings = editedBookings;
          return of(r);
        }),
        take(1),
        finalize(() => {
          this.refreshFromServer();
        })
      )
      .subscribe({
        next: () => {},
        error: () => {},
      });
  }
}
